import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { SmartField_Api_Contracts_Site_SiteDto } from '../../api';
import { MapPicker } from '../../components/MapPicker';
import { SITE_SERIAL_NUMBER_REGEX, SITE_SERIAL_NUMBER_FORMAT_MESSAGE } from '../../lib/validation/constants';

type SiteFormModalProps = {
  mode: 'create' | 'edit';
  site?: SmartField_Api_Contracts_Site_SiteDto;
  onClose: () => void;
  onSubmit: (data: SmartField_Api_Contracts_Site_SiteDto) => void;
  isSubmitting: boolean;
  error: Error | null;
};

type ValidationErrors = {
  name?: string;
  serialNumber?: string;
};

export function SiteFormModal({ mode, site, onClose, onSubmit, isSubmitting, error }: SiteFormModalProps) {
  const [formData, setFormData] = useState<SmartField_Api_Contracts_Site_SiteDto>({
    id: site?.id,
    name: site?.name || '',
    serialNumber: site?.serialNumber || '',
    siteType: site?.siteType || 'Stationary',
    location: site?.location || null,
    shapeCoordinates: site?.shapeCoordinates || null,
    startedAtUtc: site?.startedAtUtc || null,
    stoppedAtUtc: site?.stoppedAtUtc || null,
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Site name is required';
    }

    if (!formData.serialNumber || formData.serialNumber.trim() === '') {
      errors.serialNumber = 'Serial number is required';
    } else if (!SITE_SERIAL_NUMBER_REGEX.test(formData.serialNumber)) {
      errors.serialNumber = SITE_SERIAL_NUMBER_FORMAT_MESSAGE;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof SmartField_Api_Contracts_Site_SiteDto, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      location: { x: lng, y: lat }
    }));
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'create' ? 'Create New Site' : 'Edit Site'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <div className="font-medium mb-1">Error</div>
              <div>{error.message || 'An error occurred while saving the site'}</div>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Site Name *
            </label>
            <input
              id="name"
              type="text"
              required
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900 ${
                validationErrors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {validationErrors.name && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Serial Number * <span className="text-xs text-gray-500">(Format: CC-SSS, e.g., DK-Fou)</span>
            </label>
            <input
              id="serialNumber"
              type="text"
              required
              disabled={mode === 'edit'}
              value={formData.serialNumber || ''}
              onChange={(e) => handleChange('serialNumber', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900 ${
                validationErrors.serialNumber ? 'border-red-500' : 'border-gray-300'
              } ${mode === 'edit' ? 'disabled:bg-gray-100 disabled:cursor-not-allowed' : ''}`}
            />
            {validationErrors.serialNumber && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.serialNumber}</p>
            )}
            {mode === 'edit' && (
              <p className="mt-1 text-sm text-gray-500">Serial number cannot be changed</p>
            )}
          </div>

          <div>
            <label htmlFor="siteType" className="block text-sm font-medium text-gray-700 mb-1">
              Site Type *
            </label>
            <select
              id="siteType"
              value={formData.siteType || 'Stationary'}
              onChange={(e) => handleChange('siteType', e.target.value)}
              disabled={mode === 'edit'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="Stationary">Stationary</option>
              <option value="Mobile">Mobile</option>
            </select>
            {mode === 'edit' && (
              <p className="mt-1 text-sm text-gray-500">Site type cannot be changed</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location * <span className="text-xs text-gray-500">(Click on map, use current location, or enter coordinates manually)</span>
            </label>
            <MapPicker
              latitude={formData.location?.y ?? null}
              longitude={formData.location?.x ?? null}
              onLocationChange={handleLocationChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude (Y)
              </label>
              <input
                id="latitude"
                type="number"
                step="any"
                value={formData.location?.y ?? ''}
                onChange={(e) => handleLocationChange(
                  e.target.value ? parseFloat(e.target.value) : 0,
                  formData.location?.x ?? 0
                )}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
              />
            </div>
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude (X)
              </label>
              <input
                id="longitude"
                type="number"
                step="any"
                value={formData.location?.x ?? ''}
                onChange={(e) => handleLocationChange(
                  formData.location?.y ?? 0,
                  e.target.value ? parseFloat(e.target.value) : 0
                )}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startedAt" className="block text-sm font-medium text-gray-700 mb-1">
                Started Date
              </label>
              <input
                id="startedAt"
                type="datetime-local"
                value={formData.startedAtUtc ? new Date(formData.startedAtUtc).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleChange('startedAtUtc', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
              />
            </div>
            <div>
              <label htmlFor="stoppedAt" className="block text-sm font-medium text-gray-700 mb-1">
                Stopped Date
              </label>
              <input
                id="stoppedAt"
                type="datetime-local"
                value={formData.stoppedAtUtc ? new Date(formData.stoppedAtUtc).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleChange('stoppedAtUtc', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {mode === 'create' ? 'Create Site' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
